import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Handle, Position, NodeProps } from 'reactflow';
import { Lock, Settings, Plus, Trash2, Edit2 } from 'lucide-react';
import { ProcessorNodeData } from '../../types/pipeline';
import { InputDefinition, InputType, DisplayType } from '../../types/pipeline';
import { usePipelineBuilderStore } from '../../store/pipelineBuilderStore';
import { useUIStore } from '../../store';
import { getArtifactTypeLabel, getArtifactTypeColor } from '../../utils/artifactTypes';

/**
 * InputProcessorNode - special node for defining pipeline-level inputs (SWRM-128)
 * This node is always first in the pipeline and cannot be deleted.
 */
export const InputProcessorNode: React.FC<NodeProps<ProcessorNodeData>> = ({ id, data, selected }) => {
  const selectNode = usePipelineBuilderStore((state) => state.selectNode);
  const updateNode = usePipelineBuilderStore((state) => state.updateNode);
  const edges = usePipelineBuilderStore((state) => state.edges);
  const theme = useUIStore((state) => state.theme);
  const setEdges = usePipelineBuilderStore((state) => state.setEdges);
  
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingInput, setEditingInput] = useState<InputDefinition | null>(null);

  // Get inputs from config - wrap in useMemo to prevent recreating array on every render
  const inputs: InputDefinition[] = useMemo(() => data.config?.inputs || [], [data.config?.inputs]);

  // MIGRATION: Auto-convert old 'file_group' type to 'list' (backwards compatibility)
  // TODO: Remove this migration code after all pipelines have been manually migrated
  useEffect(() => {
    const hasOldTypes = inputs.some((inp: any) => inp.type === 'file_group');
    if (hasOldTypes) {
      console.log('[InputProcessorNode] MIGRATION: Converting file_group → list');
      const migratedInputs = inputs.map((inp: any) => ({
        ...inp,
        type: inp.type === 'file_group' ? 'list' : inp.type
      }));
      
      // Also build output_types mapping for type-based handle colors
      const outputTypes: Record<string, string> = {};
      migratedInputs.forEach(inp => {
        outputTypes[inp.name] = inp.type;
      });
      
      updateNode(id, {
        data: {
          ...data,
          config: {
            ...data.config,
            inputs: migratedInputs
          },
          output_types: outputTypes
        }
      });
    } else {
      // Build output_types mapping even if no migration needed
      const outputTypes: Record<string, string> = {};
      inputs.forEach(inp => {
        outputTypes[inp.name] = inp.type;
      });
      
      // Only update if output_types changed
      const currentOutputTypes = data.output_types || {};
      const outputTypesChanged = JSON.stringify(currentOutputTypes) !== JSON.stringify(outputTypes);
      
      if (outputTypesChanged) {
        updateNode(id, {
          data: {
            ...data,
            output_types: outputTypes
          }
        });
      }
    }
  }, [inputs, id, data, updateNode]);

  // Debug logging
  console.log('[InputProcessorNode] Rendering:', {
    id,
    nodeType: data.processor_name,
    inputsCount: inputs.length,
    inputs: inputs,
    output_types: data.output_types,
    showAddDialog,
    configKeys: Object.keys(data.config || {}),
  });

  const handleSettingsClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    selectNode(id);
  }, [id, selectNode]);

  const handleAddInput = useCallback((e: React.MouseEvent) => {
    console.log('[InputProcessorNode] Add Input button clicked!');
    e.stopPropagation();
    e.preventDefault();
    setShowAddDialog(true);
  }, []);

  const handleEditInput = useCallback((e: React.MouseEvent, input: InputDefinition) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingInput(input);
    setShowAddDialog(true);
  }, []);

  const handleDeleteInput = useCallback((e: React.MouseEvent, inputName: string) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Remove the input from config
    const updatedInputs = inputs.filter(inp => inp.name !== inputName);
    updateNode(id, {
      data: {
        ...data,
        config: {
          ...data.config,
          inputs: updatedInputs
        }
      }
    });
    
    // Remove edges connected to this input's output handle
    const handleId = `output-${inputName}`;
    const updatedEdges = edges.filter(edge => {
      // Remove edges where source is this node and sourceHandle is the deleted input's handle
      if (edge.source === id && edge.sourceHandle === handleId) {
        return false;
      }
      return true;
    });
    
    if (updatedEdges.length !== edges.length) {
      setEdges(updatedEdges);
    }
  }, [id, inputs, data, edges, updateNode, setEdges]);

  const handleSaveInput = useCallback((input: InputDefinition) => {
    let updatedInputs: InputDefinition[];
    
    if (editingInput) {
      // Update existing input
      updatedInputs = inputs.map(inp => 
        inp.name === editingInput.name ? input : inp
      );
    } else {
      // Add new input
      updatedInputs = [...inputs, input];
    }
    
    updateNode(id, {
      data: {
        ...data,
        config: {
          ...data.config,
          inputs: updatedInputs
        }
      }
    });
    
    setShowAddDialog(false);
    setEditingInput(null);
  }, [id, inputs, editingInput, data, updateNode]);

  const handleCancelDialog = useCallback(() => {
    setShowAddDialog(false);
    setEditingInput(null);
  }, []);

  return (
    <>
      <div
        className={`
          bg-green-50 dark:bg-green-900/20
          border-2 
          ${selected ? 'border-blue-500 shadow-lg' : 'border-green-500'}
          rounded-lg w-[280px] shadow-md
          transition-all duration-200
          nodrag nopan
        `}
        onClick={(e) => {
          // Prevent ReactFlow's onNodeClick from selecting this node
          // Only the gear icon should trigger selection
          e.stopPropagation();
        }}
        data-testid="input-processor-node"
      >
        {/* Header */}
        <div className="bg-green-500 dark:bg-green-600 text-white px-3 py-2 rounded-t-md flex items-center justify-between relative">
          <button
            onClick={handleSettingsClick}
            className="p-1 hover:bg-green-600 dark:hover:bg-green-700 rounded transition-colors"
            title="Open detailed settings"
          >
            <Settings className="w-4 h-4" />
          </button>
          
          <div className="flex items-center gap-2 flex-1">
            <span className="font-semibold text-sm">
              {data.name || 'Pipeline Inputs'}
            </span>
            <Lock className="w-3 h-3" />
          </div>
          
          {/* Execution flow handle (next_node) - right side */}
          <Handle
            type="source"
            position={Position.Right}
            id="next_node"
            className="execution-handle !w-3 !h-3 !border-2 !border-slate-500 dark:!border-gray-300"
            style={{
              background: theme === 'dark' ? '#e5e5e5' : '#64748b',
              borderRadius: '2px',
              right: '-6px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
            }}
            data-testid="next-node-handle"
          />
        </div>

        {/* Body */}
        <div className="bg-white dark:bg-gray-800 p-3 rounded-b-md">
          {/* Description */}
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            Define pipeline inputs that will be provided when creating workflows
          </div>

          {/* Inputs list */}
          {inputs.length === 0 ? (
            <div className="text-xs text-gray-500 dark:text-gray-400 italic mb-2">
              No inputs defined yet
            </div>
          ) : (
            <div className="space-y-2 mb-2 relative">
              {inputs.map((input, index) => (
                <div
                  key={input.name}
                  className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 relative"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1">
                      {input.name}
                      {input.required && (
                        <span className="text-red-500 text-xs">*</span>
                      )}
                      <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                        {getArtifactTypeLabel(input.type)}
                      </span>
                    </div>
                    {input.description && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {input.description}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1 ml-2">
                    <button
                      onClick={(e) => handleEditInput(e, input)}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors nodrag nopan"
                      title="Edit input"
                    >
                      <Edit2 className="w-3 h-3 text-gray-600 dark:text-gray-300" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteInput(e, input.name)}
                      className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors nodrag nopan"
                      title="Delete input"
                    >
                      <Trash2 className="w-3 h-3 text-red-600 dark:text-red-400" />
                    </button>
                  </div>

                  {/* Output handle for this input - positioned at the vertical center of this input item */}
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`output-${input.name}`}
                    className="!w-2 !h-2 !border"
                    style={{
                      background: getArtifactTypeColor(input.type),
                      borderColor: getArtifactTypeColor(input.type),
                      right: '-8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      position: 'absolute',
                    }}
                    data-testid={`output-handle-${input.name}`}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Add input button */}
          {(() => {
            console.log('[InputProcessorNode] Add Input button rendering');
            return null;
          })()}
          <button
            onClick={handleAddInput}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 rounded transition-colors border border-dashed border-green-300 dark:border-green-700 nodrag nopan"
            data-testid="add-input-button"
            style={{
              border: '2px dashed #22c55e',
              minHeight: '40px',
            }}
          >
            <Plus className="w-4 h-4" />
            Add Input
          </button>
        </div>
      </div>

      {/* Add/Edit Input Dialog */}
      {showAddDialog && createPortal(
        <InputDefinitionDialog
          input={editingInput}
          existingNames={inputs.map(i => i.name).filter(n => n !== editingInput?.name)}
          onSave={handleSaveInput}
          onCancel={handleCancelDialog}
        />,
        document.body
      )}
    </>
  );
};

/**
 * Dialog for adding or editing an input definition
 */
interface InputDefinitionDialogProps {
  input: InputDefinition | null;
  existingNames: string[];
  onSave: (input: InputDefinition) => void;
  onCancel: () => void;
}

const InputDefinitionDialog: React.FC<InputDefinitionDialogProps> = ({
  input,
  existingNames,
  onSave,
  onCancel
}) => {
  const [name, setName] = useState(input?.name || '');
  const [inputType, setInputType] = useState<InputType>(input?.type || 'string');
  const [displayType, setDisplayType] = useState<DisplayType>(input?.display_type || 'text');
  const [description, setDescription] = useState(input?.description || '');
  const [defaultValue, setDefaultValue] = useState(input?.default_value || '');
  const [required, setRequired] = useState(input?.required ?? true);
  const [error, setError] = useState<string>('');

  const handleSave = useCallback(() => {
    // Validate name
    const cleanName = name.trim().toLowerCase().replace(/\s+/g, '_');
    
    if (!cleanName) {
      setError('Name is required');
      return;
    }
    
    if (!/^[a-z0-9_]{1,32}$/.test(cleanName)) {
      setError('Name must be lowercase alphanumeric or underscore, max 32 characters');
      return;
    }
    
    const reservedNames = ['title', 'pipeline_id', 'workflow_id', 'specification', 'workflow_context', 'github_clone_config', 'uploaded_files'];
    if (reservedNames.includes(cleanName)) {
      setError(`"${cleanName}" is a reserved name`);
      return;
    }
    
    if (existingNames.includes(cleanName)) {
      setError(`An input named "${cleanName}" already exists`);
      return;
    }
    
    // List/file types cannot have default value
    if ((inputType === 'list' || inputType === 'file') && defaultValue) {
      setError('File and list inputs cannot have a default value');
      return;
    }
    
    // Validate select format
    if (displayType === 'select') {
      const trimmedValue = defaultValue.trim();
      
      // For required inputs, options must be provided
      if (required && !trimmedValue) {
        setError('Dropdown options are required. Use format: |Option 1|Option 2|*Default');
        return;
      }
      
      // If options are provided, validate format
      if (trimmedValue) {
        const parts = trimmedValue.split('|').filter(s => s.trim());
        
        if (parts.length < 2) {
          setError('Dropdown must have at least 2 options');
          return;
        }
        
        const defaultCount = parts.filter(p => p.trim().startsWith('*')).length;
        if (defaultCount > 1) {
          setError('Only one option can be marked as default with *');
          return;
        }
        
        // For required inputs, must have a default
        if (required && defaultCount === 0) {
          setError('Required dropdown must have a default option marked with *');
          return;
        }
      }
    }
    
    // Create input definition
    const inputDef: InputDefinition = {
      name: cleanName,
      type: inputType,
      display_type: displayType,
      description: description.trim() || undefined,
      default_value: inputType === 'string' ? (defaultValue.trim() || undefined) : undefined,
      required
    };
    
    onSave(inputDef);
  }, [name, inputType, displayType, description, defaultValue, required, existingNames, onSave]);

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
      onClick={(e) => {
        // Stop propagation to prevent ReactFlow from seeing this event
        e.stopPropagation();
        // Close dialog if clicking on backdrop itself
        if (e.target === e.currentTarget) {
          onCancel();
        }
      }}
      onMouseDown={(e) => {
        // Prevent mousedown from reaching ReactFlow (which would deselect/interact with nodes)
        e.stopPropagation();
      }}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 p-6"
        onClick={(e) => {
          // Stop propagation to prevent ReactFlow from seeing this event
          // DON'T preventDefault() - we want checkboxes and other form elements to work!
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          // Prevent mousedown from reaching ReactFlow
          e.stopPropagation();
        }}
      >
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
          {input ? 'Edit Input' : 'Add Input'}
        </h3>
        
        {error && (
          <div className="mb-4 p-2 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}
        
        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="e.g., requirements"
              disabled={!!input}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Lowercase, alphanumeric, underscores only, max 16 chars
            </p>
          </div>
          
          {/* Input Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Input Type <span className="text-red-500">*</span>
            </label>
            <select
              value={inputType}
              onChange={(e) => {
                const newType = e.target.value as InputType;
                setInputType(newType);
                // Auto-adjust display type based on input type
                if (newType === 'file' || newType === 'list') {
                  setDisplayType('file');
                  setDefaultValue('');
                } else if (newType === 'json') {
                  setDisplayType('textarea');
                  setDefaultValue('');
                } else if (newType === 'string' && displayType === 'file') {
                  setDisplayType('text');
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="string">Text</option>
              <option value="file">File</option>
              <option value="list">List</option>
              <option value="json">JSON</option>
            </select>
          </div>
          
          {/* Display Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Display Type <span className="text-red-500">*</span>
            </label>
            <select
              value={displayType}
              onChange={(e) => setDisplayType(e.target.value as DisplayType)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              disabled={inputType !== 'string'}
            >
              {inputType === 'string' && (
                <>
                  <option value="text">Text (single line)</option>
                  <option value="textarea">Textarea (multi-line)</option>
                  <option value="select">Dropdown/Select</option>
                </>
              )}
              {(inputType === 'file' || inputType === 'list') && (
                <option value="file">File Upload</option>
              )}
              {inputType === 'json' && (
                <option value="textarea">JSON Editor</option>
              )}
            </select>
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Optional description"
            />
          </div>
          
          {/* Default Value (string only) */}
          {inputType === 'string' && displayType === 'select' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Options {!required && '(optional)'}
              </label>
              <input
                type="text"
                value={defaultValue}
                onChange={(e) => setDefaultValue(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="|Option 1|Option 2|*Default Option|Option 3"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Use | to separate options, prefix with * to mark default (e.g., |dev|staging|*prod)
              </p>
            </div>
          )}
          
          {inputType === 'string' && displayType !== 'select' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Default Value
              </label>
              <textarea
                value={defaultValue}
                onChange={(e) => setDefaultValue(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Optional default value"
                rows={3}
              />
            </div>
          )}
          
          {/* Required */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="required"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="required" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              Required (must be provided when creating workflow)
            </label>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
          >
            {input ? 'Save' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
};

